// 現在のアクター（識別情報）を返す Context。
//
// SessionSnapshot から CurrentActor を導出するため、SDK Provider の状態に依存しない。
// アバター画像の idp-file: 解決と data URL 化は本コンポーネント内で実行する。

import { VIEWER_DISPLAY_NAME_MAX_LENGTH, isViewerColor } from "@refinement-canvas/domain";
import {
  createMeidenConnectApiAccessTokenProvider,
  resolveIdpFileWithAccessTokenRefresh,
  useMeidenConnectTokenRefreshSignal,
} from "@osakabe/auth-service-provider";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuthSession } from "./auth-session-context";
import { sanitizeAvatarImageUrl, usePortableAvatarImageUrl } from "./avatar-image-url";
import { getWebConfig } from "../env";
import { isValidHexColor, normalizeHexColor } from "../lib/hex-color";
import {
  VIEWER_COLOR_PREFERENCE_STORAGE_KEY,
  loadViewerColor,
  pickViewerColor,
  saveViewerColor,
} from "../lib/viewer-color-preference";

export interface CurrentActor {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarImageUrl?: string | undefined;
  readonly color: string;
}

export interface CurrentActorState {
  readonly actor: CurrentActor;
  readonly status: "loading" | "ready" | "error";
  readonly error?: Error | undefined;
  /** マイカラーを楽観更新し、ブラウザ保存まで成功したかを返す。 */
  readonly updateColor: (color: string) => boolean;
  /** 参照専用 actor では false。 */
  readonly colorEditable: boolean;
}

interface ProviderProps {
  readonly children: ReactNode;
}

const MOCK_ACTOR: CurrentActor = {
  userId: "mock-canvas-user",
  displayName: "Mock 利用者",
  color: "#1e63d4",
};

const CurrentActorContext = createContext<CurrentActorState | null>(null);

export function MockCurrentActorProvider({ children }: ProviderProps): JSX.Element {
  const { color, updateColor } = useEditableViewerColor(MOCK_ACTOR.userId);
  const state = useMemo<CurrentActorState>(
    () => ({ actor: { ...MOCK_ACTOR, color }, status: "ready", updateColor, colorEditable: true }),
    [color, updateColor]
  );
  return <CurrentActorContext.Provider value={state}>{children}</CurrentActorContext.Provider>;
}

// 参照専用の埋め込みモード用アクター。プロフィール/アバター解決（idp-file: 経路）は行わず、
// 静的な値を返す。埋め込みではプレゼンス送信やコメント投稿を行わないため識別情報は最小限でよい。
const EMBED_ACTOR: CurrentActor = {
  userId: "",
  displayName: "",
  color: pickViewerColor("embed"),
};

export function EmbedCurrentActorProvider({ children }: ProviderProps): JSX.Element {
  const state = useMemo<CurrentActorState>(
    () => ({
      actor: EMBED_ACTOR,
      status: "ready",
      updateColor: () => false,
      colorEditable: false,
    }),
    []
  );
  return <CurrentActorContext.Provider value={state}>{children}</CurrentActorContext.Provider>;
}

/**
 * useAuthSession() の SessionSnapshot から CurrentActor を導出するプロバイダー。
 *
 * 旧 RequiredCurrentActorProvider が SDK Provider 配下で `useMeidenConnectUser` を呼んでいた
 * 経路を置き換える。SDK Provider が main tree から外れたため、アバター画像 (idp-file:) の解決は
 * SessionSnapshot の avatarImageUrl と、`@osakabe/auth-service-provider` の
 * `resolveIdpFileWithAccessTokenRefresh` を組み合わせて実行する。
 */
export function SessionBackedCurrentActorProvider({ children }: ProviderProps): JSX.Element {
  const { user } = useAuthSession();
  const config = getWebConfig();
  const serviceOrigin = config.serviceOrigin ?? window.location.origin;

  const apiAccessTokenProvider = useMemo(() => {
    if (config.authServiceOrigin === undefined || user === undefined) {
      return undefined;
    }

    return createMeidenConnectApiAccessTokenProvider({
      authServiceOrigin: config.authServiceOrigin,
      serviceOrigin,
      userId: user.userId,
    });
  }, [config.authServiceOrigin, serviceOrigin, user]);

  const tokenRefreshSignal = useMeidenConnectTokenRefreshSignal();

  // idp-file: アバターは認証サービスへ署名 URL を発行してから取得する必要がある。
  // SDK Provider が main tree に無いため、package が提供する refresh 対応版を直接利用する。
  const resolveIdpFileWithRefresh = useMemo(() => {
    const authServiceOrigin = config.authServiceOrigin;
    if (authServiceOrigin === undefined || apiAccessTokenProvider === undefined) {
      return undefined;
    }

    return (idpFileUrl: string) =>
      resolveIdpFileWithAccessTokenRefresh(idpFileUrl, {
        authServiceOrigin,
        getAccessToken: apiAccessTokenProvider,
      });
  }, [apiAccessTokenProvider, config.authServiceOrigin]);

  const avatarImageUrl = usePortableAvatarImageUrl(
    user?.avatarImageUrl,
    resolveIdpFileWithRefresh,
    tokenRefreshSignal
  );
  const { color, updateColor } = useEditableViewerColor(user?.userId ?? "");

  const state = useMemo<CurrentActorState>(() => {
    if (user === undefined) {
      // 認証復旧中など SessionSnapshot が無い瞬間は、UI を壊さず最低限の placeholder を返す。
      return {
        actor: {
          userId: "",
          displayName: "",
          color: "#1a1a2e",
        },
        status: "loading",
        updateColor,
        colorEditable: false,
      };
    }

    const displayName = normalizeCurrentActorDisplayName(user.name, user.email ?? "");
    const actor: CurrentActor = {
      userId: user.userId,
      displayName,
      avatarImageUrl: sanitizeAvatarImageUrl(avatarImageUrl),
      color,
    };
    return { actor, status: "ready", updateColor, colorEditable: true };
  }, [avatarImageUrl, color, updateColor, user]);

  return <CurrentActorContext.Provider value={state}>{children}</CurrentActorContext.Provider>;
}

export function useCurrentActorState(): CurrentActorState {
  const state = useContext(CurrentActorContext);
  if (state === null) {
    throw new Error("useCurrentActorState() は CurrentActorProvider 配下で呼び出してください。");
  }
  return state;
}

export function useCurrentActor(): CurrentActor {
  return useCurrentActorState().actor;
}

function useEditableViewerColor(userId: string): {
  readonly color: string;
  readonly updateColor: (color: string) => boolean;
} {
  const [colorState, setColorState] = useState(() => ({
    userId,
    color: userId.length > 0 ? loadViewerColor(userId) : pickViewerColor(userId),
  }));
  const colorStateRef = useRef(colorState);

  const effectiveColor =
    colorState.userId === userId
      ? colorState.color
      : userId.length > 0
        ? loadViewerColor(userId)
        : pickViewerColor(userId);
  if (colorState.userId !== userId) {
    setColorState({ userId, color: effectiveColor });
  }
  colorStateRef.current = { userId, color: effectiveColor };

  useEffect(() => {
    if (userId.length === 0) return undefined;
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== null && event.key !== VIEWER_COLOR_PREFERENCE_STORAGE_KEY) return;
      const nextColor = loadViewerColor(userId);
      colorStateRef.current = { userId, color: nextColor };
      setColorState({ userId, color: nextColor });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [userId]);

  const updateColor = useCallback(
    (nextColor: string): boolean => {
      if (userId.length === 0 || !isValidHexColor(nextColor)) return false;
      const normalized = normalizeHexColor(nextColor);
      if (!isViewerColor(normalized)) return false;
      const previous =
        colorStateRef.current.userId === userId
          ? colorStateRef.current.color
          : loadViewerColor(userId);

      // 先に表示 state を更新し、保存に失敗した場合だけ直前色へ戻す。
      colorStateRef.current = { userId, color: normalized };
      setColorState({ userId, color: normalized });
      if (saveViewerColor(userId, normalized)) return true;

      colorStateRef.current = { userId, color: previous };
      setColorState({ userId, color: previous });
      return false;
    },
    [userId]
  );

  return { color: effectiveColor, updateColor };
}

export function normalizeCurrentActorDisplayName(name: string | undefined, email: string): string {
  const candidate = normalizeDisplayNamePart(name);
  const fallback = normalizeDisplayNamePart(email) || "利用者";
  const displayName = candidate.length > 0 ? candidate : fallback;
  // WebSocket の viewer.sync schema と同じ上限に揃え、長い氏名やメールで送信失敗しないようにする。
  return displayName.slice(0, VIEWER_DISPLAY_NAME_MAX_LENGTH);
}

export function resolveCurrentActorAvatarImageUrl(input: {
  readonly hasProfileRecord: boolean;
  readonly profileAvatarImageUrl: string | null | undefined;
  readonly tokenAvatarImageUrl: string | null | undefined;
}): string | undefined {
  // 認証復旧直後は ID トークン側の iconUrl が空になることがあるため、
  // IdP の User テーブルに最新プロフィールが取れている場合はそちらを正本にする。
  if (input.hasProfileRecord) {
    return normalizeAvatarImageUrl(input.profileAvatarImageUrl);
  }

  return normalizeAvatarImageUrl(input.tokenAvatarImageUrl);
}

function normalizeDisplayNamePart(value: string | undefined): string {
  return value?.trim().replace(/[\u0000-\u001f\u007f]/gu, "") ?? "";
}

function normalizeAvatarImageUrl(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}
